import React from "react";
import { Spinner } from "react-bootstrap";
import type { DadosSindicatoDetalhe } from "../../../../../services";
import type { SecaoDocumento } from "../../../../../domain/shipping/models";
import { secaoChaveParaCriterioNaAnalise } from "../../../../../domain/shipping/indiceDestaque";
import type { DocumentoAgrupado } from "../../V1/Tabs/VisaoGeralTab/VisaoGeralBlocks";

const OPCOES_TIPODOC = [
  {
    valor: "RelatorioDebitos",
    label: "Relatório de títulos a receber (Hi Gestor)",
  },
  {
    valor: "LiquidacaoDebitos",
    label: "Liquidação de títulos (Banco)",
  },
];

export function formatarDataEnvio(iso: string | null | undefined): string {
  if (!iso || !String(iso).trim()) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso);
  return new Intl.DateTimeFormat("pt-BR", {
    dateStyle: "short",
    timeStyle: "short",
  }).format(d);
}

export function resolveTituloComprovante(tituloLinha2 = "ENVIADA"): string {
  const linha = (tituloLinha2 ?? "ENVIADA").trim();
  if (!linha || linha.toUpperCase() === "ENVIADA") {
    return "COMPROVANTE DE DOCUMENTAÇÃO ENVIADA";
  }
  return `COMPROVANTE DE DOCUMENTAÇÃO ${linha.toUpperCase()}`;
}

export function buildCamposExtrasCadastro(
  dadosSindicato: DadosSindicatoDetalhe | null,
): { label: string; value: string }[] {
  const d0 = dadosSindicato;
  if (!d0) return [];
  const out: { label: string; value: string }[] = [];
  const push = (label: string, v: string | null | undefined) => {
    const t = v?.trim();
    if (t) out.push({ label, value: t });
  };
  push("Sigla", d0.sigla);
  push("Regional", d0.regional);
  push("Grupo", d0.grupo);
  const pres = d0.presidente ?? d0.nomePresidente;
  push("Presidente", pres);
  push("Responsável pelo preenchimento", d0.responsavelPreenchimento);
  return out;
}

export type MapSecoesOptions = {
  paraExportacaoPdf?: boolean;
  downloadingArquivoId?: number | null;
  isDownloadingArquivo?: boolean;
  onDownloadArquivo?: (id: number, label: string) => void;
};

function pushLinhaArquivo(
  conteudo: (string | React.ReactElement)[],
  arquivoRespostaCampoId: number | undefined,
  nome: string | undefined,
  options?: MapSecoesOptions,
) {
  const id = arquivoRespostaCampoId;
  const n = nome?.trim();

  if (options?.paraExportacaoPdf) {
    if (n) {
      conteudo.push(`Arquivo: ${n}`);
      return;
    }
    conteudo.push("Nenhum documento anexado para este campo.");
    return;
  }

  if (typeof id === "number" && id > 0) {
    const label = n || "arquivo";
    const isDownloadingCurrent = options?.downloadingArquivoId === id;
    conteudo.push(
      <p key={`arq-${id}`}>
        <strong>Arquivo:</strong>{" "}
        <button
          type="button"
          className="btn btn-link p-0 align-baseline consulta-envio-arquivo-link"
          onClick={() => options?.onDownloadArquivo?.(id, label)}
          disabled={Boolean(options?.isDownloadingArquivo)}
          aria-busy={isDownloadingCurrent}
        >
          {label}
          {isDownloadingCurrent ? (
            <>
              {" "}
              <Spinner animation="border" size="sm" />
              {" Baixando..."}
            </>
          ) : null}
        </button>
      </p>,
    );
    return;
  }
  if (n) {
    conteudo.push(`Arquivo: ${n}`);
    return;
  }
  conteudo.push("Nenhum documento anexado para este campo.");
}

function appendPerguntaResposta(
  secao: SecaoDocumento,
  conteudo: (string | React.ReactElement)[],
) {
  const pergunta = secao.pergunta?.trim() || "";
  const valor = (secao.initialPerguntaValor ?? "").trim();
  if (!pergunta && !valor) return;
  if (pergunta) {
    const q =
      pergunta.endsWith("?") || pergunta.endsWith("?*") ? pergunta : `${pergunta}?`;
    conteudo.push(q);
    if (valor) conteudo.push(valor);
  } else if (valor) {
    conteudo.push(valor);
  }
}

function mapAdimplenciaDocumento(
  secao: SecaoDocumento,
  options?: MapSecoesOptions,
): DocumentoAgrupado {
  const conteudo: (string | React.ReactElement)[] = [];
  const meta = secao.adimplenciaMeta;
  const i = meta?.initial;
  if (!meta || !i) {
    return { titulo: secao.titulo, conteudo: [""] };
  }

  conteudo.push(
    "Qual é a forma de cobrança da mensalidade associativa do Sindicato?",
  );
  conteudo.push(i.formaCobrancaValorUI?.trim() || "Não informado");

  if (i.observacaoGeral?.trim()) {
    conteudo.push(`Observação: ${i.observacaoGeral.trim()}`);
  }

  conteudo.push("Quantas empresas associadas tem seu Sindicato?*");
  conteudo.push(
    `Resposta: ${i.quantidadeAssociados > 0 ? String(i.quantidadeAssociados) : "Não informado"}`,
  );

  conteudo.push("Tipo do documento*");
  const tipoLabel =
    OPCOES_TIPODOC.find((t) => t.valor === i.tipoDocumentoValorUI)?.label ??
    i.tipoDocumentoValorUI?.trim() ??
    "Não informado";
  conteudo.push(`Resposta: ${tipoLabel}`);

  const maxA = meta.maxAssociados ?? 10;
  const qtd = Math.max(0, Math.min(maxA, i.quantidadeAssociados || 0));
  const associados = i.associados ?? [];
  for (let idx = 0; idx < qtd; idx++) {
    const assoc = associados[idx];
    const labelNum = String(idx + 1).padStart(2, "0");
    conteudo.push(`Associado ${labelNum}`);
    conteudo.push(`CNPJ: ${assoc?.cnpj?.trim() || "Não preenchido"}`);
    conteudo.push(
      `Razão Social: ${assoc?.nomeSocial?.trim() || "Não preenchido"}`,
    );
    const idArq = assoc?.arquivosIds?.[0];
    const nomeArq =
      assoc?.arquivoNomeExibicao?.trim() || assoc?.arquivoNome?.trim();
    pushLinhaArquivo(conteudo, idArq, nomeArq, options);
    if (assoc?.observacao?.trim()) {
      conteudo.push(`Observação: ${assoc.observacao.trim()}`);
    }
  }

  return { titulo: secao.titulo, conteudo };
}

function mapSecaoDocumento(
  secao: SecaoDocumento,
  options?: MapSecoesOptions,
): DocumentoAgrupado {
  if (secao.id === "adimplencia") {
    return mapAdimplenciaDocumento(secao, options);
  }

  if (secao.tipo === "somente-analista") {
    return {
      titulo: secao.titulo,
      conteudo: ["Indicadores de análise (somente na tela de análise V2)."],
    };
  }

  const conteudo: (string | React.ReactElement)[] = [];
  const valor = (secao.initialPerguntaValor ?? "").trim();
  const isSimNao = valor === "SIM" || valor === "NÃO";

  if (secao.tipo === "pergunta") {
    appendPerguntaResposta(secao, conteudo);
  } else if (secao.tipo === "upload") {
    const nome =
      secao.initialArquivoNomeExibicao?.trim() ||
      secao.initialArquivoName?.trim();
    const idArq = secao.initialArquivosIds?.[0];
    pushLinhaArquivo(conteudo, idArq, nome, options);
    const obs = secao.initialObservacao?.trim();
    if (obs) conteudo.push(`Observação: ${obs}`);
  } else if (secao.tipo === "pergunta-com-upload") {
    appendPerguntaResposta(secao, conteudo);
    const nome =
      secao.initialArquivoNomeExibicao?.trim() ||
      secao.initialArquivoName?.trim();
    const idArq = secao.initialArquivosIds?.[0];
    if (!(isSimNao && valor === "NÃO")) {
      if (nome || (typeof idArq === "number" && idArq > 0)) {
        pushLinhaArquivo(conteudo, idArq, nome, options);
      } else {
        conteudo.push("Nenhum documento anexado para este campo.");
      }
    }
    const obs = secao.initialObservacao?.trim();
    if (obs) conteudo.push(`Observação: ${obs}`);
  }

  return { titulo: secao.titulo, conteudo };
}

export function mapSecoesParaDocumentos(
  secoes: SecaoDocumento[],
  options?: MapSecoesOptions,
  parecerPorSecaoId?: Record<string, (string | React.ReactElement)[]>,
): DocumentoAgrupado[] {
  return secoes.map((secao) => {
    const doc = mapSecaoDocumento(secao, options);

    const chaveAnalise = secaoChaveParaCriterioNaAnalise(secao);
    const extra = parecerPorSecaoId?.[chaveAnalise] ?? parecerPorSecaoId?.[secao.id];
    if (!extra?.length) return doc;
    return { ...doc, conteudo: [...doc.conteudo, ...extra] };
  });
}

function stripLeadingNumeroTitulo(titulo: string): string {
  return titulo.replace(/^\s*\d+\.\s*/, "").trim();
}

export function numerarCriteriosDocumentos(
  documentos: DocumentoAgrupado[],
): DocumentoAgrupado[] {
  return documentos.map((doc, index) => ({
    ...doc,
    titulo: `${index + 1}. ${stripLeadingNumeroTitulo(doc.titulo)}`,
  }));
}
